import { useEffect, useRef, useCallback, useState } from 'react';
import { SESSION_TIMEOUT_CONFIG } from 'shared/constants/session-timeout';

interface IUseIdleTimerParams {
    isActive: boolean;
    onIdle: () => void;
    onPrompt: () => void;
    onActive?: () => void;
}

interface IUseIdleTimerReturn {
    resetTimer: () => void;
}

export const useIdleTimer = ({ isActive, onIdle, onPrompt, onActive }: IUseIdleTimerParams): IUseIdleTimerReturn => {
    const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const promptTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const lastActivityRef = useRef<number>(Date.now());
    const throttleRef = useRef<boolean>(false);

    const [isPromptShown, setIsPromptShown] = useState(false);
    const isPromptShownRef = useRef(false);

    const clearTimers = useCallback(() => {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        if (promptTimerRef.current) clearTimeout(promptTimerRef.current);
    }, []);

    const startTimers = useCallback(() => {
        clearTimers();
        const { IDLE_TIMEOUT_MS, WARNING_BEFORE_TIMEOUT_MS } = SESSION_TIMEOUT_CONFIG;

        promptTimerRef.current = setTimeout(() => {
            isPromptShownRef.current = true;
            setIsPromptShown(true);
            onPrompt();
        }, IDLE_TIMEOUT_MS - WARNING_BEFORE_TIMEOUT_MS);

        idleTimerRef.current = setTimeout(() => {
            onIdle();
        }, IDLE_TIMEOUT_MS);
    }, [clearTimers, onIdle, onPrompt]);

    const resetTimer = useCallback(() => {
        lastActivityRef.current = Date.now();
        localStorage.setItem(SESSION_TIMEOUT_CONFIG.STORAGE_KEY, String(lastActivityRef.current));

        if (isPromptShownRef.current) {
            isPromptShownRef.current = false;
            setIsPromptShown(false);
            onActive?.();
        }

        startTimers();
    }, [startTimers, onActive]);

    const handleActivity = useCallback(() => {
        if (throttleRef.current) return;
        throttleRef.current = true;
        setTimeout(() => {
            throttleRef.current = false;
        }, SESSION_TIMEOUT_CONFIG.THROTTLE_MS);

        resetTimer();
    }, [resetTimer]);

    const handleStorageSync = useCallback(
        (event: StorageEvent) => {
            if (event.key !== SESSION_TIMEOUT_CONFIG.STORAGE_KEY || !event.newValue) return;

            const otherTabActivity = Number(event.newValue);
            if (otherTabActivity > lastActivityRef.current) {
                lastActivityRef.current = otherTabActivity;
                if (isPromptShownRef.current) {
                    isPromptShownRef.current = false;
                    setIsPromptShown(false);
                    onActive?.();
                }
                startTimers();
            }
        },
        [startTimers, onActive],
    );

    useEffect(() => {
        if (!isActive) {
            clearTimers();
            return;
        }
        resetTimer();
        window.addEventListener('storage', handleStorageSync);
        return () => {
            clearTimers();
            window.removeEventListener('storage', handleStorageSync);
        };
    }, [isActive]);

    // Activity listeners — attached ONLY while the prompt is NOT shown.
    // The moment the prompt opens, these are removed entirely, so mousemove
    // while reaching for a button literally cannot reset the timer or
    // dismiss the modal. Only the explicit "Stay Signed In" button (which
    // calls resetTimer directly) can bring them back.
    useEffect(() => {
        if (!isActive || isPromptShown) return;

        const events = SESSION_TIMEOUT_CONFIG.ACTIVITY_EVENTS;
        events.forEach((eventName) => window.addEventListener(eventName, handleActivity, { passive: true }));

        return () => {
            events.forEach((eventName) => window.removeEventListener(eventName, handleActivity));
        };
    }, [isActive, isPromptShown, handleActivity]);

    return { resetTimer };
};
